import { Text } from "@mantine/core"
import { PathProxy } from "../../../../../utils/forms-proxy"
import { match } from "ts-pattern"
import {
  AbsentApprovalConfig,
  AbsentApprovalConfigNodeDetails_Case_ContractTypeOrContractTypeCategoryCondition,
  AbsentApprovalConfigNodeDetails_Case_DepartmentOrWorkareaCondition,
} from "../../../../../@client/Theasoft_Absences_Config"
import { DepartmentOrWorkareaTreeSelect } from "../../../../../-components/department-workarea-tree-select"
import { DepartmentOrWorkAreaId } from "../../../../../@client/Absents"
import { useAbsenceConfigContext } from "./AbsenceContext"
import { useTranslation } from "react-i18next"
import { ContractTypeOrCategorySelect } from "./contract-type-or-category-tree-select"
import { useUpdateOrCreateApprovalConfigForm } from "./useUpdateOrCreateApprovalConfigForm"

const map = {
  treeNodeIdToDepartmentIdOrWorkareaId: (id: string) => {
    const values = id.split("Id")
    const c = values[0] + "Id"
    if (c !== "DepartmentId" && c !== "WorkAreaId") {
      throw new Error(`id must be DepartmentId or WorkAreaId but got ${c}`)
    }
    const field = parseInt(values[1])
    if (isNaN(field)) {
      throw new Error(`expected a number but got ${values[1]}`)
    }
    return { Case: c, Fields: field } satisfies DepartmentOrWorkAreaId
  },
  departmentIdOrWorkareaIdToNodeId: (v: DepartmentOrWorkAreaId) =>
    `${v.Case}${v.Fields}`,
}

export function ConditionPropertiesEditor({
  nodeId,
}: {
  allowEditing?: boolean
  nodeId: string
  seqIndex?: number
  defaultMode?: "editing" | "readonly"
}) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const { isReadonly } = useAbsenceConfigContext()
  const node = form.values.nodes.find((n) => n.nodeId === nodeId)
  const index = form.values.nodes.findIndex((n) => n.nodeId === nodeId)

  const { t } = useTranslation("app_server")

  if (node === undefined || index === undefined) {
    return <Text c="red">{t("CouldntFindX", { x: `nodeId ${nodeId}` })}</Text>
  }

  return match(node.details)
    .with(
      { Case: "ContractTypeOrContractTypeCategoryCondition" },
      (details) => {
        const detailsPath = _.nodes[index].details as PathProxy<
          AbsentApprovalConfig,
          // here we need to specify the type of the details though this is unsafe, I dont know if there is a more elegant way
          AbsentApprovalConfigNodeDetails_Case_ContractTypeOrContractTypeCategoryCondition
        >

        return (
          <ContractTypeOrCategorySelect
            disabled={isReadonly}
            label={t("ContractTypes")}
            initialCheckedState={details.Fields.values}
            onCheckedStateChange={(v) => {
              form.setFieldValue(detailsPath.Fields.values._PATH_, v)
            }}
          />
        )
      }
    )
    .with({ Case: "DepartmentOrWorkareaCondition" }, (details) => {
      const detailsPath = _.nodes[index].details as PathProxy<
        AbsentApprovalConfig,
        // here we need to specify the type of the details though this is unsafe, I dont know if there is a more elegant way
        AbsentApprovalConfigNodeDetails_Case_DepartmentOrWorkareaCondition
      >
      const selected = details.Fields.values.map(
        map.departmentIdOrWorkareaIdToNodeId
      )

      return (
        <DepartmentOrWorkareaTreeSelect
          disabled={isReadonly}
          label={t("Departments")}
          onCheckedStateChange={(v) => {
            const values = v.map(map.treeNodeIdToDepartmentIdOrWorkareaId)
            form.setFieldValue(detailsPath.Fields.values._PATH_, values)
          }}
          initialCheckedState={selected}
          withModule={false}
          withRoot={false}
        />
      )
    })

    .otherwise(() => (
      <Text c="red">
        This is not a conditional node. This seems to be a bug
      </Text>
    ))
}
